import { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import {
    Modal,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Form,
    Select,
} from "@gpstrackit/react-controls";

//Notification
import { NotificationManager } from "Components/Notifications";

//Actions
import {
    editSwapSimCardGetDeviceRequest,
    searchSimProviderAccountsRequest,
    editSwapSimCardGetSimDetailsRequest,
    editSwapSimCardUpdateRequest,
    clearRedux,
} from 'Redux/actions';

//Styles
import './index.scss';

const EditSwapSimCard = (props) => {
    const { getDevice, tableRef } = props;
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const formRef = useRef(null);
    //Local states
    const [imei, setImei] = useState("");
    const [esn, setEsn] = useState("");
    const [simProviderAccount, setSimProviderAccount] = useState(null);
    const [iccid, setIccid] = useState("");
    const [simPhone, setSimPhone] = useState("");
    const [simProviderAccountsList, setSimProviderAccounts] = useState([]);
    const [manufacturer, setManufacturer] = useState("");

    //Redux states
    const {
        loading,
        openModal,
        result,
        devices,
        device,
        simDetails,
    } = useSelector(state => state.editSwapSimCardRedux)
    const { simProviderAccounts } = useSelector(state => state.generalRedux);

    const closeModal = () => {
        dispatch(clearRedux("EDIT_SWAP_SIM_CARD"));
        setImei("");
        setEsn("");
        setSimProviderAccount(null);
        setIccid("");
        setSimPhone("");
        setSimProviderAccounts([]);
        setManufacturer("");
    }

    const loadPhone = () => {
        dispatch(editSwapSimCardGetSimDetailsRequest(iccid))
    }

    const applyChanges = () => {
        let payload = {
            id: device?.id,
        }
        if (manufacturer?.toUpperCase() !== 'Global star'.toUpperCase()) {
            payload.simProviderAccountId = simProviderAccount?.id;
            payload.iccid = iccid
            payload.msisdn = simPhone
        }

        dispatch(editSwapSimCardUpdateRequest(payload));
    }

    useEffect(() => {
        if (openModal) {
            dispatch(searchSimProviderAccountsRequest());
        }
        if (openModal && devices && devices.length) {
            let currentDevice = devices[0]?.id
            dispatch(editSwapSimCardGetDeviceRequest(currentDevice));
            dispatch(clearRedux("EDIT_SWAP_SIM_CARD", { devices: [] }))
        }

        if (simDetails?.result) {
            let currentSimDetails = JSON.parse(simDetails?.result)
            if (currentSimDetails?.msisdn) {
                setSimPhone(currentSimDetails?.msisdn || '');
            }
            dispatch(clearRedux("EDIT_SWAP_SIM_CARD", { simDetails: null }));
        }
        if (simDetails?.result === null) {
            NotificationManager.warning(
                messages['edit_swap_sim_card_sim_details_error'],
                messages['edit_swap_sim_card_title'],
                3000
            );
            dispatch(clearRedux("EDIT_SWAP_SIM_CARD", { simDetails: null }));
        }
        if (result) {
            const { update } = result;
            if (update) {
                NotificationManager.success(
                    messages['edit_swap_sim_card_saved_successfully'],
                    messages['edit_swap_sim_card_title'],
                    3000
                );
                tableRef && tableRef?.current?.getData();
                getDevice && getDevice();
                closeModal();
            } else {
                NotificationManager.error(
                    messages['edit_swap_sim_card_the_was_an_error_in_the_save'],
                    messages['edit_swap_sim_card_title'],
                    3000
                );
            }
            dispatch(clearRedux("EDIT_SWAP_SIM_CARD", { result: null }));
        }
    }, [openModal, devices, simDetails, result]);

    useEffect(() => {
        if (simProviderAccounts && simProviderAccounts.length > 0) {
            let current = simProviderAccounts.map((item) => ({ ...item, label: `${item.name} ${item?.simProvider?.name ? `- ${item?.simProvider?.name}` : ""}`, simProvider: item?.simProvider?.name }));
            setSimProviderAccounts(current);
        }
    }, [simProviderAccounts])

    useEffect(() => {
        if (device && device.id) {
            setImei(device?.imei || "");
            setEsn(device?.esn || "");
            setIccid(device?.simCard?.iccId || "");
            setSimPhone(device?.simCard?.msisdn || "");
            setManufacturer(device?.deviceModel?.manufacturer?.name || "");
            if (device?.simCard?.simProviderAccount?.name) {
                let value = {
                    id: device?.simCard?.simProviderAccount.id,
                    label: `${device?.simCard?.simProviderAccount?.name} ${device?.simCard?.simProviderAccount?.simProvider?.name ? `- ${device?.simCard?.simProviderAccount?.simProvider?.name}` : ""}`,
                    simProvider: device?.simCard?.simProviderAccount?.simProvider?.name
                }
                setSimProviderAccount(value)
            }
        }
    }, [device])


    return (
        <Modal
            open={openModal}
            title={messages['edit_swap_sim_card_title']}
            position="right"
            size="small"
            handleClose={closeModal}
            loading={loading}
        >
            <ModalBody>
                <div style={{ padding: '0 10px' }}>
                    <Form
                        ref={formRef}
                        onValidSubmit={() => applyChanges()}
                    >
                        <div className="edit-swap-disabled-input" style={{ marginBottom: 5 }}>
                            <Input
                                label={messages['edit_swap_sim_card_device_imei']}
                                placeholder={messages['edit_swap_sim_card_device_imei']}
                                value={imei}
                                onChange={(value) => setImei(value)}
                                disabled
                            />
                        </div>
                        <div className="edit-swap-disabled-input" style={{ marginBottom: 5 }}>
                            <Input
                                label={messages['edit_swap_sim_card_device_esn']}
                                placeholder={messages['edit_swap_sim_card_device_esn']}
                                value={esn}
                                onChange={(value) => setEsn(value)}
                                disabled
                            />
                        </div>
                        {
                            manufacturer?.toUpperCase() !== 'Global star'.toUpperCase() &&
                            <div style={{ marginBottom: 5 }}>
                                <Select
                                    id="edit-swap-sim-provider-account"
                                    label={messages['edit_swap_sim_card_sim_provider_account']}
                                    placeholder={messages['edit_swap_sim_card_sim_provider_account']}
                                    value={simProviderAccount}
                                    options={simProviderAccountsList || []}
                                    onChange={(value) => setSimProviderAccount(value)}
                                    valueField="id"
                                    textField="label"
                                    isClearable
                                    requiredMessage={messages['required_field']}
                                    required
                                />
                            </div>
                        }
                        {
                            manufacturer?.toUpperCase() !== 'Global star'.toUpperCase() &&
                            <>
                                <div style={{ marginBottom: 5 }}>
                                    <Input
                                        label="ICCID"
                                        placeholder="ICCID"
                                        value={iccid}
                                        onChange={(value) => setIccid(value)}
                                        requiredMessage={messages['required_field']}
                                        maxLength={20}
                                        minLength={14}
                                        regex={"^[a-zA-Z0-9_.-]*$"}
                                        minLengthMessage={messages["edit_swap_sim_card_iccid_minlength"]}
                                        required
                                    />
                                </div>
                                <div style={{ marginBottom: 5 }}>
                                    <Input
                                        type="text"
                                        label={messages['edit_swap_sim_card_sim_sim_phone']}
                                        placeholder={messages['edit_swap_sim_card_sim_sim_phone']}
                                        value={simPhone}
                                        onChange={(value) => setSimPhone(value)}
                                        maxLength={15}
                                        minLength={8}
                                        regex={"^[0-9]*$"}
                                        minLengthMessage={messages["devices_import_valid_msisdn_minlength"]}
                                        required
                                        requiredMessage={messages['required_field']}
                                    />
                                </div>
                            </>

                        }
                        {
                            simProviderAccount?.simProvider === "AT&T" &&
                            <Button
                                className="btn-dark-blue"
                                onClick={(e) => {
                                    e.preventDefault();
                                    loadPhone();
                                }}
                            >
                                {messages['edit_swap_sim_card_load_phone_number']}
                            </Button>
                        }
                    </Form>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    className="btn-cancel"
                    onClick={(e) => {
                        e.preventDefault();
                        closeModal();
                    }}
                >
                    {messages['common_cancel']}
                </Button>
                <Button
                    className="btn-dark-blue"
                    onClick={() => formRef?.current?.submit()}
                >
                    {messages['save']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default EditSwapSimCard; 